import type {
  ModuleViewCounters,
  ModuleViewItem,
  ModuleViewListResponse
} from '@/components/designSystem/QueueViews/types'
import type { DSAttendanceEffectiveSummary } from '@/types/ds-attendance.types'
import type {
  ActivityRequestItem,
  ActivityRequestPayload,
  ActivityRequestQuery,
  ActivityRequestTimelineEvent,
  ActivityRequestTimelineLink,
  ActivityRequestTimelineResponse
} from '@/types/activity-requests.types'

/**
 * FRONTEIRA ÚNICA com a API `ds-attendance/operations/activity-requests/*` (backend PR5).
 *
 * Todo nome de campo/parâmetro que atravessa a rede mora AQUI: os tipos `Api*` descrevem o
 * que o backend manda, os `map*` traduzem para os tipos da tela (`@/types/activity-requests.types`)
 * e `buildActivityRequestParams` escreve a query. Se o backend renomear um campo, a mudança é
 * neste arquivo — componentes, composables e testes de tela não mudam.
 *
 * Conferido contra o backend PR5 (bng-health/minhaescala-backend#2898). Os formatos coincidem,
 * exceto `payload.effectiveSummary`: o backend manda o `EffectiveCheckView` cru
 * (`checkin`/`checkinSource.kind`), e a tela usa o formato por perna (`{ value, source }`).
 */

/* ------------------------------------------------------------------ resposta */

/** `EffectiveCheckView` do backend (`doctor-station-effective-check`), só o que a tela lê. */
export interface ApiEffectiveSourceView {
  kind: string
  method?: string | null
}

export interface ApiEffectiveCheckView {
  checkin: string | null
  checkout: string | null
  checkinSource: ApiEffectiveSourceView | null
  checkoutSource: ApiEffectiveSourceView | null
  timezone?: string | null
  registrationStatus?: string | null
}

export type ApiActivityRequestPayload = Omit<ActivityRequestPayload, 'effectiveSummary'> & {
  effectiveSummary: ApiEffectiveCheckView | null
}
export type ApiActivityRequestItem = ModuleViewItem<ApiActivityRequestPayload>
export type ApiActivityRequestListResponse = ModuleViewListResponse<ApiActivityRequestPayload>
export type ApiActivityRequestCounters = ModuleViewCounters
export type ApiActivityRequestTimelineLink = ActivityRequestTimelineLink
export type ApiActivityRequestTimelineEvent = ActivityRequestTimelineEvent
export type ApiActivityRequestTimelineResponse = ActivityRequestTimelineResponse

/** Perna sem horário efetivo vira `null`: o card a mostra como pendente. */
const mapEffectiveLeg = (
  value: string | null | undefined,
  source: ApiEffectiveSourceView | null | undefined
): DSAttendanceEffectiveSummary['checkin'] =>
  value ? { value, source: source?.kind ?? null } : null

function mapEffectiveSummary(
  raw: ApiEffectiveCheckView | null | undefined
): DSAttendanceEffectiveSummary | null {
  if (!raw) return null
  return {
    checkin: mapEffectiveLeg(raw.checkin, raw.checkinSource),
    checkout: mapEffectiveLeg(raw.checkout, raw.checkoutSource)
  }
}

/** Campo ausente (backend antigo ou item legado) vira `null`, nunca `undefined`. */
const orNull = <T>(value: T | null | undefined): T | null => value ?? null

function mapStation(
  raw: Partial<ApiActivityRequestPayload['station']> | null | undefined
): ActivityRequestPayload['station'] {
  const station = raw ?? {}
  return {
    doctorStationId: orNull(station.doctorStationId),
    stationId: orNull(station.stationId),
    referenceDate: station.referenceDate ?? '',
    startAt: orNull(station.startAt),
    endAt: orNull(station.endAt),
    timezone: station.timezone ?? '',
    shiftName: orNull(station.shiftName)
  }
}

function mapReopen(
  raw: ApiActivityRequestPayload['reopen'] | undefined
): ActivityRequestPayload['reopen'] {
  if (!raw) return null
  return {
    previousContainerId: raw.previousContainerId,
    rejectionReason: orNull(raw.rejectionReason),
    rejectionReasonLabel: orNull(raw.rejectionReasonLabel),
    rejectionJustification: orNull(raw.rejectionJustification),
    rejectedAt: orNull(raw.rejectedAt),
    rejectedByName: orNull(raw.rejectedByName),
    previousSubmittedByName: orNull(raw.previousSubmittedByName),
    previousSubmittedAt: orNull(raw.previousSubmittedAt),
    cycle: raw.cycle ?? 1
  }
}

function mapPayload(raw: ApiActivityRequestPayload): ActivityRequestPayload {
  return {
    containerId: raw.containerId,
    status: raw.status,
    requestedLeg: raw.requestedLeg,
    requestChannel: orNull(raw.requestChannel),
    registrationMode: orNull(raw.registrationMode),
    openingRule: orNull(raw.openingRule),
    sendWindowHours: orNull(raw.sendWindowHours),
    openedAt: orNull(raw.openedAt),
    sendDeadlineAt: orNull(raw.sendDeadlineAt),
    isOverdue: raw.isOverdue === true,
    reopenCount: raw.reopenCount ?? 0,
    operationalGroup: orNull(raw.operationalGroup),
    station: mapStation(raw.station),
    effectiveSummary: mapEffectiveSummary(raw.effectiveSummary),
    payment: {
      hasAntecipe: raw.payment?.hasAntecipe === true,
      hasInCash: raw.payment?.hasInCash === true
    },
    reopen: mapReopen(raw.reopen),
    policy: orNull(raw.policy)
  }
}

function mapItem(raw: ApiActivityRequestItem): ActivityRequestItem {
  return {
    module: raw.module,
    id: raw.id,
    entityId: raw.entityId,
    stage: raw.stage,
    sla: raw.sla ?? null,
    title: raw.title,
    subtitle: raw.subtitle ?? null,
    occurredAt: raw.occurredAt,
    timezone: raw.timezone,
    actor: raw.actor ?? null,
    location: raw.location,
    badges: raw.badges ?? [],
    value: raw.value ?? null,
    detailRef: raw.detailRef,
    payload: mapPayload(raw.payload)
  }
}

export function mapActivityRequestListResponse(
  raw: ApiActivityRequestListResponse
): ModuleViewListResponse<ActivityRequestPayload> {
  return {
    module: raw.module,
    serverNow: raw.serverNow,
    stages: raw.stages ?? [],
    items: (raw.items ?? []).map(mapItem),
    count: raw.count ?? 0,
    page: raw.page,
    limit: raw.limit
  }
}

export function mapActivityRequestCounters(raw: ApiActivityRequestCounters): ModuleViewCounters {
  return {
    module: raw.module,
    serverNow: raw.serverNow,
    byStage: raw.byStage ?? {},
    byView: raw.byView ?? {}
  }
}

export function mapActivityRequestTimelineResponse(
  raw: ApiActivityRequestTimelineResponse
): ActivityRequestTimelineResponse {
  return {
    containerId: raw.containerId,
    links: (raw.links ?? []).map((link) => ({
      containerId: link.containerId,
      status: link.status,
      previousContainerId: link.previousContainerId ?? null,
      rejectionReason: link.rejectionReason ?? null,
      createdAt: link.createdAt ?? null,
      submittedAt: link.submittedAt ?? null,
      reviewedAt: link.reviewedAt ?? null
    })),
    events: (raw.events ?? []).map((event) => ({
      auditId: event.auditId,
      containerId: event.containerId,
      actionType: event.actionType,
      at: event.at ?? null,
      justification: event.justification ?? null,
      actorName: event.actorName ?? null,
      values: event.values ?? null
    }))
  }
}

/* ------------------------------------------------------------------ query */

/**
 * O ÚNICO montador da query de `activity-requests` — lista, contadores e export leem o
 * mesmo objeto, então uma tela e um arquivo nunca divergem (mesma disciplina de
 * `appendContainerFilters`).
 *
 * NÃO reaproveita `appendScalarParams` do `DsAttendanceService`: ele descarta `false`, e
 * aqui `false` é um valor ("Sem Antecipe", "Não reaberta" — M1 decisão 8). Só `null`
 * (tanto faz) fica de fora.
 */
export function buildActivityRequestParams(
  query: ActivityRequestQuery,
  options: { paginate: boolean; includeView: boolean }
): URLSearchParams {
  const params = new URLSearchParams()

  query.healthUnitIds.forEach((id) => params.append('healthUnitIds', String(id)))
  query.scheduleIds.forEach((id) => params.append('scheduleIds', String(id)))
  query.shiftIds.forEach((id) => params.append('shiftIds', String(id)))
  // Repetido, um valor por grupo. O `toArray` do backend ainda quebra cada valor em vírgula
  // (ver "Lacunas do spec"): nome de grupo com vírgula precisa de decisão no backend.
  query.operationalGroups.forEach((group) => params.append('operationalGroups', group))
  query.registrationModes.forEach((mode) => params.append('registrationModes', mode))
  query.requestedLeg.forEach((leg) => params.append('requestedLeg', leg))

  const scalars: Record<string, string | null> = {
    startDate: query.startDate,
    endDate: query.endDate,
    deadlineFrom: query.deadlineFrom,
    deadlineTo: query.deadlineTo,
    search: query.search.trim() || null,
    orderBy: query.orderBy
  }
  for (const [key, value] of Object.entries(scalars)) {
    if (value !== null && value !== '') params.append(key, value)
  }

  const triStates: Record<string, boolean | null> = {
    reopened: query.reopened,
    hasAntecipe: query.hasAntecipe,
    hasInCash: query.hasInCash
  }
  for (const [key, value] of Object.entries(triStates)) {
    if (value !== null) params.append(key, value ? 'true' : 'false')
  }

  if (options.includeView) params.append('view', query.view)
  if (options.paginate) {
    params.append('page', String(query.page))
    params.append('limit', String(query.limit))
  }

  return params
}
